import {AppFeature, AppWorksWith} from '../types/AppConstants';

export type WorksWithOption = 'CMS' | 'Opal';
export type FeaturesOption = 'OCP Source' | 'OCP Destination' | 'Opal Tools' | 'CMS UI Extensions';
type UIOption = WorksWithOption | FeaturesOption;

export const isWorksWithFilter = (
  value: AppFeature | AppWorksWith
): value is AppWorksWith =>
  value === AppWorksWith.CMS || value === AppWorksWith.OPAL;

export const isFeaturesFilter = (
  value: AppFeature | AppWorksWith
): value is AppFeature =>
  value === AppFeature.SOURCE_APP ||
  value === AppFeature.DESTINATION_APP ||
  value === AppFeature.OPAL_TOOL_APP ||
  value === AppFeature.CMS_UI_EXTENSION_APP;

/**
 * Utility class for mapping between UI labels and AppFeature enum values.
 *
 * This provides a centralized, extensible way to manage the relationship between
 * user-facing filter labels and backend feature types.
 */
export class FilterMapper {
  /**
   * Convert a UI label to its corresponding AppFeature enum value.
   *
   * @param uiLabel The UI label selected by the user
   * @returns The corresponding AppFeature or AppWorksWith
   */
  public static toAppFilter(uiLabel: UIOption): AppFeature | AppWorksWith {
    return this.UI_TO_FILTER_MAP[uiLabel];
  }

  /**
   * Convert an AppFeature to its corresponding UI label.
   *
   * @param filter The AppFeature or AppWorksWith enum value
   * @returns The UI label to display
   */
  public static toUILabel(filter: AppFeature | AppWorksWith): UIOption {
    return this.FILTER_TO_UI_MAP[filter];
  }

  /**
   * Get all available options for display in the UI.
   *
   * @returns Array of all UIOption values
   */
  public static getAllOptions(): UIOption[] {
    return Object.keys(this.UI_TO_FILTER_MAP) as UIOption[];
  }

  /**
   * Get all available filters options
   *
   * @returns Array of all filter values
   */
  public static getAllFilters(): Array<AppFeature | AppWorksWith> {
    return Object.values(this.UI_TO_FILTER_MAP);
  }
  /**
   * Check if a UI option represents "show all apps" (no filtering).
   *
   * @param uiLabel The UI label to check
   * @returns true if this option means "no filter"
   */
  public static isShowAll(uiLabel: UIOption): boolean {
    return this.UI_TO_FILTER_MAP[uiLabel] === null;
  }

  /**
   * Map from UI labels to AppFeature enum values.
   * null value means "show all apps" (no actual filtering).
   */
  private static readonly UI_TO_FILTER_MAP: Record<UIOption, AppFeature | AppWorksWith> = {
    'OCP Source': AppFeature.SOURCE_APP,
    'OCP Destination': AppFeature.DESTINATION_APP,
    'Opal Tools': AppFeature.OPAL_TOOL_APP,
    'CMS UI Extensions': AppFeature.CMS_UI_EXTENSION_APP,
    'CMS': AppWorksWith.CMS,
    'Opal': AppWorksWith.OPAL,
  };

  /**
   * Reverse map from AppFeature to UI label.
   * Used for displaying the currently selected filter.
   */
  private static readonly FILTER_TO_UI_MAP: Record<AppFeature | AppWorksWith, UIOption> = {
    [AppFeature.SOURCE_APP]: 'OCP Source',
    [AppFeature.DESTINATION_APP]: 'OCP Destination',
    [AppFeature.OPAL_TOOL_APP]: 'Opal Tools',
    [AppFeature.CMS_UI_EXTENSION_APP]: 'CMS UI Extensions',
    [AppWorksWith.CMS]: 'CMS',
    [AppWorksWith.OPAL]: 'Opal',
  };
}
